/**
 * v2 权限常量定义
 * @description 定义系统所有的权限标识和权限组（v2 专用）
 *
 * ⚠️ 统一管理原则：
 * 所有权限字符串必须在此文件中定义为常量，
 * 禁止在 PermissionService.ts、sidebarNavigationConfig.ts 或其他文件中直接硬编码。
 */

export const PERMISSIONS = {
  // 通配符权限（管理员）
  WILDCARD: '*:*:*',

  // ==================== 用户相关权限 ====================
  USER: {
    // 基础操作权限
    READ: 'user:read',
    UPDATE: 'user:update',
    CREATE: 'user:create',
    DELETE: 'user:delete',

    // 个人资料
    PROFILE: {
      VIEW: 'user:profile:view',
      EDIT: 'user:profile:edit',
    },

    // 模型管理
    MODELS: {
      VIEW: 'user:models:view',
      CREATE: 'user:models:create',
      EDIT: 'user:models:edit',
      DELETE: 'user:models:delete',
    },

    // 收藏管理
    FAVORITES: {
      VIEW: 'user:favorites:view',
      ADD: 'user:favorites:add',
      REMOVE: 'user:favorites:remove',
    },

    // 浏览历史
    HISTORY: {
      VIEW: 'user:history:view',
      CLEAR: 'user:history:clear',
    },

    // 订单管理
    ORDERS: {
      VIEW: 'user:orders:view',
      CREATE: 'user:orders:create',
      PAY: 'user:orders:pay',
    },

    // 钱包
    WALLET: {
      VIEW: 'user:wallet:view',
      RECHARGE: 'user:wallet:recharge',
      WITHDRAW: 'user:wallet:withdraw',
    },

    // 财务
    FINANCE: {
      VIEW: 'user:finance:view',
      WITHDRAW: 'user:finance:withdraw',
      DEPOSIT: 'user:finance:deposit',
      EARNINGS: 'user:finance:earnings',
    },

    // 消息
    MESSAGES: {
      VIEW: 'user:messages:view',
      READ: 'user:messages:read',
      DELETE: 'user:messages:delete',
    },

    // 设置
    SETTINGS: {
      VIEW: 'user:settings:view',
      SECURITY: 'user:settings:security',
      BINDING: 'user:settings:binding',
      NOTIFICATION: 'user:settings:notification',
    },

    // 身份认证
    IDENTITY: {
      VIEW: 'user:identity:view',
      APPLY: 'user:identity:apply',
    },

    // 任务
    TASKS: {
      VIEW: 'user:tasks:view',
      ACCEPT: 'user:tasks:accept',
      CREATE: 'user:tasks:create',
      MANAGE: 'user:tasks:manage',
    },

    // 系统
    SYSTEM: {
      TEST: 'user:system:test',
    },
  },

  // ==================== 模型相关权限 ====================
  MODEL: {
    VIEW: 'model:view',
    UPLOAD: 'model:upload',
    MANAGE: 'model:manage',
    DOWNLOAD: 'model:download',
  },

  // ==================== 任务相关权限 ====================
  TASK: {
    VIEW: 'task:view',
    PUBLISH: 'task:publish',
    MANAGE: 'task:manage',
    BID: 'task:bid',
  },

  // ==================== 管理员权限 ====================
  ADMIN: {
    READ: 'admin:read',
    WRITE: 'admin:write',
    DELETE: 'admin:delete',
  },

  // ==================== 系统管理权限 ====================
  SYSTEM: {
    MANAGE: 'system:manage',
    SETTINGS: 'system:settings',
  },

  // ==================== 用户管理权限 ====================
  USER_MANAGE: 'user:manage',
} as const

// ==================== 权限组定义 ====================

export const PERMISSION_GROUPS = {
  // 通用权限（所有登录用户）
  COMMON: [PERMISSIONS.USER.PROFILE.VIEW, PERMISSIONS.MODEL.VIEW],

  // 基础用户权限
  USER_BASE: [PERMISSIONS.USER.PROFILE.EDIT, PERMISSIONS.MODEL.DOWNLOAD],

  // 已认证用户权限
  CERTIFIED: [PERMISSIONS.USER.TASKS.CREATE],

  // 设计师专属权限
  DESIGNER_EXCLUSIVE: [
    PERMISSIONS.USER.TASKS.ACCEPT,
    PERMISSIONS.MODEL.UPLOAD,
    PERMISSIONS.MODEL.MANAGE,
  ],

  // 制造商专属权限
  MANUFACTURER_EXCLUSIVE: [
    PERMISSIONS.USER.TASKS.ACCEPT,
    PERMISSIONS.USER.FINANCE.WITHDRAW,
  ],

  // 用户业务权限（所有登录用户的完整业务权限集）
  USER_BUSINESS: [
    PERMISSIONS.USER.MODELS.VIEW,
    PERMISSIONS.USER.MODELS.CREATE,
    PERMISSIONS.USER.MODELS.EDIT,
    PERMISSIONS.USER.MODELS.DELETE,
    PERMISSIONS.USER.FAVORITES.VIEW,
    PERMISSIONS.USER.FAVORITES.ADD,
    PERMISSIONS.USER.FAVORITES.REMOVE,
    PERMISSIONS.USER.HISTORY.VIEW,
    PERMISSIONS.USER.HISTORY.CLEAR,
    PERMISSIONS.USER.ORDERS.VIEW,
    PERMISSIONS.USER.ORDERS.CREATE,
    PERMISSIONS.USER.ORDERS.PAY,
    PERMISSIONS.USER.WALLET.VIEW,
    PERMISSIONS.USER.WALLET.RECHARGE,
    PERMISSIONS.USER.FINANCE.VIEW,
    PERMISSIONS.USER.MESSAGES.VIEW,
    PERMISSIONS.USER.MESSAGES.READ,
    PERMISSIONS.USER.MESSAGES.DELETE,
    PERMISSIONS.USER.SETTINGS.VIEW,
    PERMISSIONS.USER.SETTINGS.SECURITY,
    PERMISSIONS.USER.SETTINGS.BINDING,
    PERMISSIONS.USER.SETTINGS.NOTIFICATION,
    PERMISSIONS.USER.IDENTITY.VIEW,
    PERMISSIONS.USER.IDENTITY.APPLY,
    PERMISSIONS.USER.TASKS.VIEW,
  ],
} as const

// 类型定义
export type PermissionString = string
export type FlatPermissionList = string[]

export const PermissionUtils = {
  // 简单的权限检查工具函数
  hasPermission(userPermissions: string[], requiredPermission: string): boolean {
    if (userPermissions.includes(PERMISSIONS.WILDCARD))
      return true
    return userPermissions.includes(requiredPermission)
  },
}
